import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import CampaniaDatosForm, { type CampaignFormValues } from './CampaniaDatosForm';
import CampaniaPosiciones from './CampaniaPosiciones';
import CampaniaFormulario from './CampaniaFormulario';
import {
  countSchedulePositions, deleteSchedule, getCampaign, listSchedules, updateCampaign, upsertSchedule,
  type ScheduleWithPdv,
} from '../../api/campaigns';
import { scheduleSchema, type CampaignFormInput } from '../../domain/campaignSchema';
import { puedeAsignarSupervisores, puedeEditar } from '../../domain/permissions';
import ImportarPlantillaHorarios from '../../components/ImportarPlantillaHorarios';
import ResumenEquipo from '../../components/ResumenEquipo';
import DinamicasCampania from '../../components/DinamicasCampania';
import CampaniaSupervisores from './CampaniaSupervisores';
import CalendarioFechasSueltas from '../../components/CalendarioFechasSueltas';
import BuscadorPdv from '../../components/BuscadorPdv';
import DarDeBajaHorario from '../../components/DarDeBajaHorario';
import {
  contarJornadasAntesDe, listBajasDeCampania, ultimaBajaPorHorario, type BajaRegistro, type BajaResultado,
} from '../../api/bajaHorario';
import { refreshSchedulePositions } from '../../api/positions';
import { textoAjusteJornadas } from '../../domain/ajusteJornadas';
import { ddmmaaaa, fechasFueraDeRango, resumenDiasDelHorario } from '../../domain/calendario';
import {
  cambioDeVigenciaQueNoVaPorFormulario, textoVigencia, validarVigencia,
} from '../../domain/vigenciaHorario';
import { useAuth } from '../../components/AuthProvider';
import type { CampaignRow } from '../../lib/db-types';

// Índice + 1 = día ISO (1=lunes … 7=domingo)
const WEEKDAY_LABELS = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

type Tab = 'datos' | 'horarios' | 'posiciones' | 'formulario' | 'dinamicas' | 'equipo' | 'supervisores';

const TABS: { key: Tab; label: string }[] = [
  { key: 'datos', label: 'Datos' },
  { key: 'horarios', label: 'Horarios' },
  { key: 'dinamicas', label: 'Dinámicas' },
  { key: 'equipo', label: 'Equipo' },
  { key: 'supervisores', label: 'Supervisores' },
  { key: 'posiciones', label: 'Posiciones' },
  { key: 'formulario', label: 'Formulario' },
];

export default function CampaniaDetalle() {
  const { id } = useParams<{ id: string }>();
  const { roles } = useAuth();
  // La programación (datos, horarios, posiciones y formulario) es del comercial dueño y del
  // jefe. El coordinador y el supervisor la LEEN para saber qué tienen que cubrir: desde la
  // migración 0019 la RLS ya no les deja escribir posiciones, así que la pantalla tiene que
  // decir lo mismo que dice la base.
  const soloLectura = !puedeEditar('campanias', roles);
  const [tab, setTab] = useState<Tab>('datos');
  const [campaign, setCampaign] = useState<CampaignRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    try {
      setCampaign(await getCampaign(id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar la campaña');
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);

  if (error) return <p className="p-4 text-red-600">{error}</p>;
  if (!campaign) return <p className="p-4 text-gray-500">Cargando…</p>;

  const initial: CampaignFormValues = {
    name: campaign.name ?? '',
    analyticAccount: campaign.analytic_account ?? '',
    client: campaign.client,
    brand: campaign.brand,
    type: campaign.type as CampaignFormValues['type'],
    description: campaign.description ?? '',
    quota: campaign.quota === null ? '' : String(campaign.quota),
    startDate: campaign.start_date,
    endDate: campaign.end_date,
    coordinatorId: campaign.coordinator_id ?? '',
    status: campaign.status as CampaignFormValues['status'],
  };

  async function onSaveDatos(parsed: CampaignFormInput) {
    if (!campaign) return;
    await updateCampaign(campaign.id, {
      // Vacío se guarda como NULL: "sin cuenta analítica" es un estado real (llega después),
      // y una cadena vacía haría que el importador crea que ya tiene una.
      name: parsed.name === '' ? null : parsed.name,
      analytic_account: parsed.analyticAccount === '' ? null : parsed.analyticAccount,
      client: parsed.client,
      brand: parsed.brand,
      type: parsed.type,
      description: parsed.description === '' ? null : parsed.description,
      quota: parsed.quota,
      start_date: parsed.startDate,
      end_date: parsed.endDate,
      coordinator_id: parsed.coordinatorId,
      status: parsed.status,
    });
    setSaved(true);
    await load();
  }

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <h1 className="text-xl font-bold mb-1">{campaign.client} — {campaign.brand}</h1>
      <p className="text-sm text-gray-500 mb-4">{campaign.start_date} al {campaign.end_date}</p>
      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map((t) => (
          <button key={t.key}
            className={`px-3 py-2 rounded ${tab === t.key ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            onClick={() => { setSaved(false); setTab(t.key); }}>
            {t.label}
          </button>
        ))}
      </div>

      {soloLectura && (
        <p className="mb-3 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600">
          Ficha de consulta. La programación la carga y la corrige el comercial; los cambios
          con la campaña ya iniciada los autoriza el jefe de operaciones.
        </p>
      )}
      {tab === 'datos' && (
        <section className="border rounded p-4 bg-white">
          {saved && <p className="text-green-700 mb-2">Cambios guardados</p>}
          <CampaniaDatosForm
            initial={initial}
            submitLabel="Guardar cambios"
            onSubmit={onSaveDatos}
            soloLectura={soloLectura}
          />
        </section>
      )}
      {tab === 'horarios' && <HorariosSection campaign={campaign} soloLectura={soloLectura} />}
      {/* Dónde y cuándo corre cada dinámica. Define qué ve el celular de la gente en campo, así
          que solo entra quien puede editar la campaña. */}
      {tab === 'dinamicas' && (
        soloLectura
          ? <p className="text-sm text-gray-500">
              Configurar dinámicas es del jefe de operaciones.
            </p>
          : <DinamicasCampania campaignId={campaign.id} />
      )}
      {/* El resumen del equipo lo ve también el coordinador y el supervisor: es SU gente, y es
          justamente quien tiene que actuar sobre lo que muestra. */}
      {tab === 'equipo' && <ResumenEquipo campaignId={campaign.id} />}
      {/* A diferencia del resto de la pantalla (donde el coordinador solo LEE), acá el
          coordinador SÍ edita: pedido explícito del usuario (24-set), está más cerca del
          terreno que el comercial para decidir quién supervisa qué tienda. */}
      {tab === 'supervisores' && (
        <CampaniaSupervisores
          campaignId={campaign.id}
          soloLectura={!puedeAsignarSupervisores(roles)}
        />
      )}
      {tab === 'posiciones' && (
        <CampaniaPosiciones campaign={campaign} soloLectura={soloLectura} />
      )}
      {tab === 'formulario' && (
        <CampaniaFormulario campaign={campaign} soloLectura={soloLectura} />
      )}
    </div>
  );
}

interface ScheduleForm {
  id: string | null;
  pdvId: string;
  /** Lo que se muestra de la tienda elegida («T10108 — Metro San Miguel»). */
  pdvEtiqueta: string;
  weekdays: number[];
  extraDates: string[];
  startTime: string;
  endTime: string;
  headcount: string;
  rateAmount: string;
  rateUnit: 'jornada' | 'hora';
  /** Vigencia (0304): vacío = toda la campaña. */
  vigenteDesde: string;
  vigenteHasta: string;
}

function HorariosSection({ campaign, soloLectura }: {
  campaign: CampaignRow;
  soloLectura: boolean;
}) {
  const [schedules, setSchedules] = useState<ScheduleWithPdv[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<ScheduleForm | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  /** La última baja de cada horario, para mostrar desde cuándo y por qué. */
  const [bajas, setBajas] = useState<Map<string, BajaRegistro>>(new Map());
  const [bajaDe, setBajaDe] = useState<ScheduleWithPdv | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [lista, registradas] = await Promise.all([
        listSchedules(campaign.id),
        // Las bajas solo aportan el motivo: si no se pueden leer (la RLS se las da al dueño y al jefe),
        // la vigencia se muestra igual con sus fechas, que salen del horario.
        listBajasDeCampania(campaign.id).catch(() => [] as BajaRegistro[]),
      ]);
      setSchedules(lista);
      setBajas(ultimaBajaPorHorario(registradas));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudieron cargar los horarios');
    }
  }, [campaign.id]);

  useEffect(() => { void load(); }, [load]);

  function openNew() {
    setFormError(null);
    setForm({
      id: null, pdvId: '', pdvEtiqueta: '', weekdays: [], extraDates: [], startTime: '', endTime: '',
      headcount: '1', rateAmount: '', rateUnit: 'jornada', vigenteDesde: '', vigenteHasta: '',
    });
  }

  function openEdit(s: ScheduleWithPdv) {
    setFormError(null);
    setForm({
      id: s.id,
      pdvId: s.pdv_id,
      pdvEtiqueta: `${s.pdv.code} — ${s.pdv.name}`,
      weekdays: s.weekdays,
      extraDates: [...s.extra_dates],
      startTime: s.start_time.slice(0, 5),
      endTime: s.end_time.slice(0, 5),
      headcount: String(s.headcount),
      rateAmount: String(s.rate_amount),
      rateUnit: s.rate_unit as 'jornada' | 'hora',
      vigenteDesde: s.vigente_desde ?? '',
      vigenteHasta: s.vigente_hasta ?? '',
    });
  }

  function toggleWeekday(day: number) {
    setForm((f) => f && ({
      ...f,
      weekdays: f.weekdays.includes(day)
        ? f.weekdays.filter((d) => d !== day)
        : [...f.weekdays, day].sort((a, b) => a - b),
    }));
  }

  function toggleExtraDate(fecha: string) {
    setForm((f) => f && ({
      ...f,
      extraDates: f.extraDates.includes(fecha)
        ? f.extraDates.filter((d) => d !== fecha)
        : [...f.extraDates, fecha].sort(),
    }));
  }

  async function save() {
    if (!form) return;
    const parsed = scheduleSchema.safeParse({
      pdvId: form.pdvId,
      weekdays: form.weekdays,
      extraDates: form.extraDates,
      startTime: form.startTime,
      endTime: form.endTime,
      headcount: form.headcount.trim() === '' ? NaN : Number(form.headcount),
      rateAmount: form.rateAmount.trim() === '' ? NaN : Number(form.rateAmount),
      rateUnit: form.rateUnit,
    });
    if (!parsed.success) {
      setFormError(parsed.error.issues[0].message);
      return;
    }
    // El calendario no deja tocar días fuera de la campaña, pero un horario viejo puede traer una
    // fecha que quedó fuera si después se acortaron las fechas de la campaña. El servidor también la
    // rechaza (`insert_positions`); acá se dice cuál y por qué, antes del viaje.
    const fuera = fechasFueraDeRango(parsed.data.extraDates, campaign.start_date, campaign.end_date);
    if (fuera.length > 0) {
      setFormError(
        `${fuera.map(ddmmaaaa).join(', ')} ${fuera.length === 1 ? 'queda' : 'quedan'} fuera de la campaña, `
        + `que va del ${ddmmaaaa(campaign.start_date)} al ${ddmmaaaa(campaign.end_date)}. `
        + `${fuera.length === 1 ? 'Quítala' : 'Quítalas'} para poder guardar.`,
      );
      return;
    }
    const vigencia = { desde: form.vigenteDesde, hasta: form.vigenteHasta };
    const anterior = form.id ? schedules.find((s) => s.id === form.id) ?? null : null;
    // Una vigencia ya guardada que no se tocó no se vuelve a validar: la de un horario dado de baja
    // desde su primer día (hasta = desde − 1) no pasaría, y eso impediría corregirle la tarifa.
    const sinTocar = anterior !== null
      && (anterior.vigente_desde ?? '') === vigencia.desde && (anterior.vigente_hasta ?? '') === vigencia.hasta;
    if (!sinTocar) {
      const errorVigencia = validarVigencia(vigencia, campaign);
      if (errorVigencia) {
        setFormError(errorVigencia);
        return;
      }
    }
    if (anterior !== null && !sinTocar) {
      const cambio = cambioDeVigenciaQueNoVaPorFormulario(anterior, vigencia, campaign);
      if (cambio === 'acorta_final') {
        setFormError(
          'Para adelantar el fin de un horario usa «Dar de baja desde…» en la tabla: quita las jornadas '
          + 'desde esa fecha con su motivo y rechaza las postulaciones pendientes.',
        );
        return;
      }
      if (cambio === 'atrasa_inicio') {
        try {
          const antes = await contarJornadasAntesDe(anterior.id, vigencia.desde);
          if (antes > 0) {
            setFormError(
              `Este horario ya tiene ${antes} ${antes === 1 ? 'jornada' : 'jornadas'} antes del `
              + `${ddmmaaaa(vigencia.desde)}: no se puede correr su inicio. Para cambiar días u horario a `
              + 'partir de una fecha, da de baja este horario desde ese día y agrega uno nuevo para la misma '
              + 'tienda con «Vigente desde» ese día.',
            );
            return;
          }
        } catch (e) {
          setFormError(e instanceof Error ? e.message : 'No se pudo revisar las jornadas del horario');
          return;
        }
      }
    }
    let guardadoId: string | null = null;
    try {
      const guardado = await upsertSchedule({
        ...(form.id ? { id: form.id } : {}),
        campaign_id: campaign.id,
        pdv_id: parsed.data.pdvId,
        weekdays: parsed.data.weekdays,
        extra_dates: parsed.data.extraDates,
        start_time: parsed.data.startTime,
        end_time: parsed.data.endTime,
        headcount: parsed.data.headcount,
        rate_amount: parsed.data.rateAmount,
        rate_unit: parsed.data.rateUnit,
        // Sin tocar no viaja: el formulario puede llevar la vigencia de antes de una baja hecha en otra pestaña, y
        // mandarla la desharía sin aviso (la próxima «Generar posiciones» recrearía las jornadas quitadas).
        vigente_desde: sinTocar ? undefined : (vigencia.desde === '' ? null : vigencia.desde),
        vigente_hasta: sinTocar ? undefined : (vigencia.hasta === '' ? null : vigencia.hasta),
      });
      guardadoId = guardado?.id ?? form.id;
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'No se pudo guardar el horario');
      return;
    }
    const tienda = form.pdvEtiqueta || 'la tienda';
    setForm(null);
    setAviso(null);
    setError(null);
    // Guardar el horario ajusta sus jornadas (0325): crea las que pide y faltan y quita las abiertas sin persona que
    // ya no pide. Antes había que acordarse de «Regenerar» en otra pestaña, y el 21-oct no se hizo: Pringles quedó
    // con 63 jornadas que ningún horario pedía, y Oxxo Chronos y El Polo con horario y sin una sola jornada.
    if (guardadoId) {
      try {
        const r = await refreshSchedulePositions(guardadoId, 'Se editó el horario y ya no pide esta jornada');
        setAviso(textoAjusteJornadas(tienda, r));
      } catch (e) {
        setError(
          `El horario se guardó, pero no se pudieron ajustar sus jornadas: ${e instanceof Error ? e.message : 'error desconocido'}. `
          + 'Usa «Regenerar horario» en la pestaña Posiciones.',
        );
      }
    }
    await load();
  }

  /**
   * Eliminar un horario borra en cascada TODAS sus posiciones, y falla con un error de FK si
   * alguna ya tiene asignaciones. Por eso primero se cuenta el impacto y se dice en números
   * qué va a pasar, en vez de mostrar el mensaje crudo de Postgres.
   */
  async function remove(s: ScheduleWithPdv) {
    setError(null);
    let counts: { total: number; assigned: number };
    try {
      counts = await countSchedulePositions(s.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo verificar las posiciones del horario');
      return;
    }
    if (counts.assigned > 0) {
      setError(
        `No se puede eliminar el horario de ${s.pdv.name}: ${counts.assigned} de sus ` +
        `${counts.total} posiciones ya tienen recursos asignados. Retira o reemplaza primero ` +
        'esas asignaciones desde "Asignaciones". Si la tienda sale de la campaña a partir de una ' +
        'fecha, usa «Dar de baja desde…»: quita solo las jornadas de ahí en adelante.',
      );
      return;
    }
    const impacto = counts.total === 0
      ? 'Todavía no tiene posiciones generadas.'
      : `Se eliminarán también sus ${counts.total} posiciones generadas (todas abiertas, sin recursos asignados).`;
    if (!window.confirm(`¿Eliminar el horario de ${s.pdv.name}?\n\n${impacto}\n\nEsta acción no se puede deshacer.`)) return;
    try {
      await deleteSchedule(s.id);
      await load();
    } catch (e) {
      const raw = e instanceof Error ? e.message : '';
      const esFk = raw.includes('foreign key') || raw.includes('violates') || raw.includes('23503');
      setError(esFk
        ? `No se puede eliminar el horario de ${s.pdv.name}: alguna de sus posiciones ya tiene recursos asignados. Retira o reemplaza primero esas asignaciones.`
        : (raw || 'No se pudo eliminar el horario'));
    }
  }

  function bajaHecha(s: ScheduleWithPdv, r: BajaResultado) {
    setBajaDe(null);
    setAviso(
      `Horario de ${s.pdv.name} dado de baja desde el ${ddmmaaaa(r.desde)}: `
      + `${r.posicionesQuitadas} ${r.posicionesQuitadas === 1 ? 'jornada quitada' : 'jornadas quitadas'}`
      + (r.propuestasRechazadas > 0
        ? ` y ${r.propuestasRechazadas} ${r.propuestasRechazadas === 1 ? 'postulación pendiente rechazada' : 'postulaciones pendientes rechazadas'}`
        : '')
      + '. Las jornadas anteriores quedaron intactas.',
    );
    void load();
  }

  return (
    <section className="border rounded p-4 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="font-semibold">Horarios por PDV</h2>
        {!soloLectura && (
          <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={openNew}>
            Agregar horario
          </button>
        )}
      </div>
      {error && <p className="text-red-600 mb-2">{error}</p>}
      {aviso && <p role="status" className="text-green-700 mb-2">{aviso}</p>}
      {/* La carga masiva va ARRIBA de la tabla: en una campaña recién creada la tabla está vacía
          y es justo cuando se necesita, no después de recorrer 30 filas. */}
      {!soloLectura && (
        <div className="mb-3">
          <ImportarPlantillaHorarios campaign={campaign} onCargado={() => void load()} />
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="p-2">PDV</th><th className="p-2">Días</th><th className="p-2">Horario</th>
              <th className="p-2">Recursos</th><th className="p-2">Tarifa</th>
              <th className="p-2">Vigencia</th>
              {!soloLectura && <th className="p-2">Acciones</th>}
            </tr>
          </thead>
          <tbody>
            {schedules.length === 0 && (
              <tr><td className="p-2" colSpan={7}>Sin horarios todavía</td></tr>
            )}
            {schedules.map((s) => {
              const ultima = bajas.get(s.id);
              const vigencia = textoVigencia(s, campaign, ultima ? { desde: ultima.desde, motivo: ultima.motivo } : null);
              const sinDias = vigencia?.estado === 'sin_dias';
              return (
              <tr key={s.id} className={`border-t ${sinDias ? 'bg-gray-50 text-gray-500' : ''}`}>
                <td className="p-2">{s.pdv.code} — {s.pdv.name}</td>
                <td className="p-2"
                  title={s.extra_dates.length > 0 ? `Fechas sueltas: ${[...s.extra_dates].sort().map(ddmmaaaa).join(', ')}` : undefined}>
                  {resumenDiasDelHorario(s.weekdays, s.extra_dates)}
                </td>
                <td className="p-2">{s.start_time.slice(0, 5)}–{s.end_time.slice(0, 5)}</td>
                <td className="p-2">{s.headcount}</td>
                <td className="p-2">S/ {Number(s.rate_amount).toFixed(2)} / {s.rate_unit}</td>
                <td className="p-2">
                  {vigencia ? vigencia.texto : <span className="text-gray-400">Toda la campaña</span>}
                </td>
                {!soloLectura && (
                  <td className="p-2 flex flex-wrap gap-2">
                    <button className="text-blue-600 underline" onClick={() => openEdit(s)}>Editar</button>
                    {!sinDias && (
                      <button className="text-amber-700 underline"
                        onClick={() => { setAviso(null); setError(null); setBajaDe(s); }}>
                        Dar de baja desde…
                      </button>
                    )}
                    <button className="text-red-600 underline" onClick={() => void remove(s)}>Eliminar</button>
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded p-4 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h3 className="font-semibold mb-3">{form.id ? 'Editar horario' : 'Nuevo horario'}</h3>
            <BuscadorPdv
              elegido={form.pdvId ? { id: form.pdvId, etiqueta: form.pdvEtiqueta } : null}
              onElegir={(p) => setForm((f) => f && ({ ...f, pdvId: p?.id ?? '', pdvEtiqueta: p?.etiqueta ?? '' }))}
            />
            <div className="text-sm mb-2">
              <span className="block mb-1">Días de la semana</span>
              <div className="flex gap-2">
                {WEEKDAY_LABELS.map((label, i) => (
                  <label key={label} className="flex items-center gap-1">
                    <input type="checkbox" checked={form.weekdays.includes(i + 1)}
                      onChange={() => toggleWeekday(i + 1)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div className="text-sm mb-2">
              <span className="block mb-1">Fechas sueltas <span className="text-gray-500">(opcional)</span></span>
              <p className="mb-2 text-xs text-gray-500">
                Toca los días que quieras sumar al horario. Si no marcas ningún día de la semana
                arriba, el horario tendrá solo estas fechas.
              </p>
              <CalendarioFechasSueltas
                desde={campaign.start_date}
                hasta={campaign.end_date}
                weekdays={form.weekdays}
                seleccionadas={form.extraDates}
                onToggle={toggleExtraDate}
              />
              {/* Una fecha que quedó fuera (la campaña se acortó después) no se ve en el
                  calendario, así que no se podría quitar tocándola: se lista aparte. */}
              {fechasFueraDeRango(form.extraDates, campaign.start_date, campaign.end_date).map((f) => (
                <p key={f} className="mt-1 text-xs text-red-700">
                  {ddmmaaaa(f)} quedó fuera de la campaña.{' '}
                  <button type="button" className="underline" onClick={() => toggleExtraDate(f)}>
                    Quitar
                  </button>
                </p>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-sm">Hora inicio
                <input type="time" className="border rounded px-2 py-1 w-full" value={form.startTime}
                  onChange={(e) => setForm({ ...form, startTime: e.target.value })} />
              </label>
              <label className="text-sm">Hora fin
                <input type="time" className="border rounded px-2 py-1 w-full" value={form.endTime}
                  onChange={(e) => setForm({ ...form, endTime: e.target.value })} />
              </label>
              <label className="text-sm">Recursos por día
                <input inputMode="numeric" className="border rounded px-2 py-1 w-full" value={form.headcount}
                  onChange={(e) => setForm({ ...form, headcount: e.target.value })} />
              </label>
              <label className="text-sm">Tarifa (S/)
                <input inputMode="decimal" className="border rounded px-2 py-1 w-full" value={form.rateAmount}
                  onChange={(e) => setForm({ ...form, rateAmount: e.target.value })} />
              </label>
              <label className="text-sm">Unidad de tarifa
                <select className="border rounded px-2 py-1 w-full" value={form.rateUnit}
                  onChange={(e) => setForm({ ...form, rateUnit: e.target.value as 'jornada' | 'hora' })}>
                  <option value="jornada">Por jornada</option>
                  <option value="hora">Por hora</option>
                </select>
              </label>
            </div>
            <div className="text-sm mt-3">
              <span className="block mb-1">Vigencia <span className="text-gray-500">(opcional)</span></span>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm">Vigente desde
                  <input type="date" className="border rounded px-2 py-1 w-full" value={form.vigenteDesde}
                    min={campaign.start_date} max={campaign.end_date}
                    onChange={(e) => setForm({ ...form, vigenteDesde: e.target.value })} />
                </label>
                <label className="text-sm">Vigente hasta
                  <input type="date" className="border rounded px-2 py-1 w-full" value={form.vigenteHasta}
                    min={campaign.start_date} max={campaign.end_date}
                    onChange={(e) => setForm({ ...form, vigenteHasta: e.target.value })} />
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Vacío = toda la campaña. Para cambiar días u horario a partir de una fecha: da de baja
                este horario desde ese día y agrega uno nuevo para la misma tienda con «Vigente desde»
                ese día.
              </p>
            </div>
            {formError && <p className="text-red-600 mt-2">{formError}</p>}
            <div className="flex gap-2 mt-4 justify-end">
              <button className="px-3 py-2 bg-gray-200 rounded" onClick={() => setForm(null)}>Cancelar</button>
              <button className="px-3 py-2 bg-blue-600 text-white rounded" onClick={() => void save()}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {bajaDe && (
        <DarDeBajaHorario
          scheduleId={bajaDe.id}
          tienda={`${bajaDe.pdv.code} — ${bajaDe.pdv.name}`}
          campaign={campaign}
          onCancelar={() => setBajaDe(null)}
          onHecho={(r) => bajaHecha(bajaDe, r)}
        />
      )}
    </section>
  );
}
